import { withCompInfo } from '@wix/editor-elements-integrations'
import { getScaledFont } from '@wix/thunderbolt-becky-root'
import type { ThemeFontMap } from '@wix/thunderbolt-becky-types'
import type { CompInfo } from '@wix/thunderbolt-components-native'
import type { CheckboxGroupDefinition } from '../CheckboxGroup/CheckboxGroup'
import type { CheckboxCSSVars, CheckboxDefinition, CheckboxProps } from './Checkbox'
import { getHeightInPixels } from '@wix/editor-elements-common-utils'
import { getImpliedLink } from '@wix/link-utils'
import _ from 'lodash'

export const getCheckboxCss = ({
	compProps,
	compData,
	compLayout,
	compSingleLayout,
	hasResponsiveLayout,
	structure,
	styleProperties,
	siteFonts,
	isMobileView,
	isOneDocMigrated,
}: Pick<
	CompInfo<CheckboxDefinition | CheckboxGroupDefinition>,
	| 'compData'
	| 'compProps'
	| 'compLayout'
	| 'compSingleLayout'
	| 'hasResponsiveLayout'
	| 'structure'
	| 'styleProperties'
	| 'isMobileView'
	| 'isOneDocMigrated'
> & { siteFonts: ThemeFontMap }): CheckboxCSSVars => {
	const { alignment, buttonSize, spacing, inputMobileFontSize } = compProps
	const isRightAlignment = alignment === 'right'
	const isGroupOrLabel = structure.componentType === 'CheckboxGroup' || compData.label
	const getClassicButtonSize = () => {
		return isOneDocMigrated ? getHeightInPixels(compSingleLayout) : compLayout.height
	}
	const size = hasResponsiveLayout || isGroupOrLabel ? buttonSize : getClassicButtonSize()

	const compInfo = { compLayout, compProps, styleProperties, siteFonts, isMobileView }
	return {
		'--fnt': getScaledFont(compInfo, 'fnt', isOneDocMigrated, inputMobileFontSize),
		'--checkboxSize': `${size}px`,
		'--dir': isRightAlignment ? ('rtl' as const) : ('ltr' as const),
		'--flexDirection': isRightAlignment ? ('row-reverse' as const) : ('reverse' as const),
		'--checkmarkMargin': isRightAlignment ? `auto -${size}px auto auto` : `auto auto auto -${size}px`,
		...(isGroupOrLabel && {
			'--labelMargin': isRightAlignment ? `0 ${spacing + 1}px 0 0` : `0 0 0 ${spacing + 1}px`,
		}),
		...(!hasResponsiveLayout &&
			!isOneDocMigrated && {
			height: 'auto' as const,
		}),
	}
}

export const css = withCompInfo<CheckboxCSSVars, CheckboxDefinition>()(
	[
		'compProps',
		'compData',
		'compLayout',
		'compSingleLayout',
		'hasResponsiveLayout',
		'structure',
		'styleProperties',
		'siteFonts',
		'isMobileView',
		'isOneDocMigrated',
	],
	getCheckboxCss
)

export const checkboxCommonPropsNative = ({
	compData,
	required,
	readOnly,
	isDisabled,
	isMobileView,
}: {
	compData: any
	required: boolean
	readOnly: boolean
	isDisabled: boolean
	isMobileView: boolean
}): CheckboxProps => {
	const { value, label, checked, linkLabel, link: resolvedLink } = compData
	const link = !_.isEmpty(resolvedLink) ? resolvedLink : getImpliedLink(linkLabel, isMobileView)
	return {
		checked,
		linkLabel,
		link,
		label,
		value: value ?? '',
		required,
		readOnly,
		isDisabled,
	} as CheckboxProps
}

export const props = withCompInfo<any, CheckboxGroupDefinition>()(
	['compProps', 'compData', 'isMobileView'],
	({ compProps, compData, isMobileView }) => {
		const { required, readOnly, isDisabled } = compProps as any

		return checkboxCommonPropsNative({
			compData,
			readOnly,
			required,
			isDisabled,
			isMobileView,
		})
	}
)
